/**
 * Use the hero rail to feature 5+ promoted stories that are related. For example, Beauty steals during a major event. Use when a higher prominence and/or creative image is needed.
 *
 * @module views/components/HeroRail
 * @memberof -Common
 */
import './HeroRail.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import AsyncComponent from '@ulta/core/components/AsyncComponent/AsyncComponent';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { constants } from '@ulta/core/utils/constants/constants';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

/**
 * Represents a HeroRail component
 *
 * @method
 * @param {HeroRailProps} props - React properties passed from composition
 * @returns HeroRail
 */
export const HeroRail = function( props ){
  const {
    modules,
    partialDataCaptureData,
    nextClickAction,
    previousClickAction,
    railAdaMeta,
    rootMargin,
    root,
    threshold
  } = props;
  const HeroRailRef = useRef( null );

  if( !modules ){
    return null;
  }

  useIntersectionObserver(
    HeroRailRef,
    {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    },
    handleIntersection( props )
  );

  const railMeta = {
    nextAccessibility: nextClickAction?.accessibility?.label,
    previousAccessibility: previousClickAction?.accessibility?.label
  };

  return (
    <div className='HeroRail' ref={ HeroRailRef }>
      <GridContainer>
        <Rail
          railRef={ HeroRailRef }
          items={ modules }
          _railADAmeta={ railAdaMeta }
          _railMeta={ railMeta }
          nextClickAction={ nextClickAction }
          previousClickAction={ previousClickAction }
          sliderPerViewXL={ 'auto' }
          slidesPerGroupXL={ 1 }
          sliderPerViewLG={ 'auto' }
          slidesPerGroupLG={ 1 }
          sliderPerViewMD={ 'auto' }
          slidesPerGroupMD={ 1 }
          sliderPerViewSM={ 'auto' }
          slidesPerGroupSM={ 1 }
        >
          { modules?.length > 0 &&
            modules?.map( ( module, moduleIndex ) => (
              <Slide key={ `Rail${moduleIndex + 1} ` }>
                <AsyncComponent { ...module } />
              </Slide>
            ) ) }
        </Rail>
      </GridContainer>
    </div>
  );
};

/**
 * Default values for passed properties
 * @type {HeroRailProps}
 * @property {bool} railAccessibility='carousel' - The default value for aria-label is set to text 'carousel'.
 */
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS
};
/**
 * Property type definitions
 *
 * @typedef HeroRailProps
 * @type {object}
 * @property {string} partialDataCaptureData - Sets the data capture data
 * @property {object} previousClickAction - This is the action object for the Next button.
 * @property {object} nextClickAction - This is the action object for the Previous button.
 * @property {object} railAdaMeta - Sets carouselAccessibility.
 * @property {array} modules - Set the Item array in modules.
 *
 */
export const propTypes = {
  partialDataCaptureData: PropTypes.object,
  nextClickAction: PropTypes.object,
  previousClickAction: PropTypes.object,
  modules: PropTypes.array,
  railAdaMeta: PropTypes.object
};

HeroRail.propTypes = propTypes;
HeroRail.defaultProps = defaultProps;

export default HeroRail;
